import React from 'react';
import { Student } from '../../contexts/StudentsContext';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Separator } from "../../components/ui/separator";
import { Mail, Phone, MapPin, Calendar, GraduationCap, User, Hash } from "lucide-react";

interface ViewStudentProps {
    student: Student | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const ViewStudent: React.FC<ViewStudentProps> = ({ student, open, onOpenChange }) => {
    if (!student) return null;

    const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
        switch (status) {
            case 'actif': return 'default';
            case 'diplome': return 'secondary';
            case 'abandonne': return 'destructive';
            case 'inactif': return 'outline';
            default: return 'outline';
        }
    };

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case 'actif': return 'Actif';
            case 'diplome': return 'Diplômé';
            case 'abandonne': return 'Abandonné';
            case 'inactif': return 'Inactif';
            default: return status;
        }
    };

    const getInitials = (nom: string, prenom: string) => {
        return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(new Date(date));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Détails de l'étudiant</DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Header with Avatar */}
                    <div className="flex items-center gap-4">
                        <Avatar className="h-20 w-20">
                            <AvatarImage src={student.photo} alt={`${student.prenom} ${student.nom}`} />
                            <AvatarFallback className="text-lg">{getInitials(student.nom, student.prenom)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold">{student.prenom} {student.nom}</h3>
                            <p className="text-sm text-muted-foreground font-mono">{student.matricule}</p>
                            <div className="mt-2">
                                <Badge variant={getStatusBadgeVariant(student.statut)} className={
                                    student.statut === 'actif' ? 'bg-green-100 text-green-800 hover:bg-green-200 border-none' : ''
                                }>
                                    {getStatusLabel(student.statut)}
                                </Badge>
                            </div>
                        </div>

                        {/* Vertical Separator - only on desktop */}
                        <div className="hidden md:block w-px bg-border self-stretch" />

                        {/* Metadata */}
                        <div className="flex flex-col justify-center text-xs text-muted-foreground/60 space-y-1">
                            <p>Créé le {formatDate(student.createdAt)}</p>
                            <p>Modifié le {formatDate(student.updatedAt)}</p>
                        </div>
                    </div>

                    <Separator />

                    {/* Personal Information and Contact */}
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Personal Information */}
                        <div className="flex-1">
                            <h4 className="text-sm font-semibold text-muted-foreground mb-3">INFORMATIONS PERSONNELLES</h4>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Sexe</p>
                                        <p className="font-medium">{student.sexe === 'M' ? 'Masculin' : 'Féminin'}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Date de naissance</p>
                                        <p className="font-medium">{formatDate(student.dateNaissance)}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Lieu de naissance</p>
                                        <p className="font-medium">{student.lieuNaissance}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Vertical Separator - only on desktop */}
                        <div className="hidden md:block w-px bg-border" />

                        {/* Contact Information */}
                        <div className="flex-1">
                            <h4 className="text-sm font-semibold text-muted-foreground mb-3">CONTACT</h4>
                            <div className="space-y-4">
                                {student.telephone && (
                                    <div className="flex items-start gap-3">
                                        <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Téléphone</p>
                                            <p className="font-medium">{student.telephone}</p>
                                        </div>
                                    </div>
                                )}

                                {student.email && (
                                    <div className="flex items-start gap-3">
                                        <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Email</p>
                                            <p className="font-medium">{student.email}</p>
                                        </div>
                                    </div>
                                )}

                                {student.adresse && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Adresse</p>
                                            <p className="font-medium">{student.adresse}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Academic Information */}
                    <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3">INFORMATIONS ACADÉMIQUES</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3">
                                <GraduationCap className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Niveau</p>
                                    <p className="font-medium">{student.niveau}ème année</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <GraduationCap className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Filière</p>
                                    <p className="font-medium">{student.filiere}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Année d'admission</p>
                                    <p className="font-medium">{student.anneeAdmission}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Hash className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Matricule</p>
                                    <p className="font-medium font-mono text-sm">{student.matricule}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ViewStudent;
